/**
 * UserManagementPage Helpers
 * 사용자 관리 페이지 관련 상수 및 헬퍼 함수
 */
import { User } from '../types/user.types';
import { PRM_CODE, USE_YN, USER_TYPE } from '../../shared/config/system.config';

type PrmCodeEntry = { comCode: string; comCdName: string; val3?: string | null };

// ======== 타입 ========

export interface FilterState {
    keyword: string;
    userType: string;
    useYn: string;
    suppName: string;
}

// ======== 상수 ========

export const FILTER_LABELS = {
    KEYWORD: '이름/ID',
    USER_TYPE: '사용자 타입',
    PRM_CODE: '권한',
    USE_YN: '사용 여부',
    SUPP_NAME: '협력사명',
} as const;

export const FILTER_PLACEHOLDERS = {
    KEYWORD: '사용자명 또는 ID 입력',
    SUPP_NAME: '협력사명',
} as const;

export const BUTTON_LABELS = {
    NEW: '신규',
    SEARCH: '검색',
} as const;

export const TABLE_HEADERS: readonly { key: string; label: string; width?: string }[] = [
    { key: 'no', label: 'NO', width: '80px' },
    { key: 'userId', label: '사용자 ID' },
    { key: 'userName', label: '사용자명' },
    { key: 'userType', label: '사용자 타입' },
    { key: 'prmCode', label: '권한' },
    { key: 'suppType', label: '협력사 타입' },
    { key: 'phoneNum', label: '전화번호' },
    { key: 'emailInfo', label: '이메일' },
    { key: 'useYn', label: '사용 여부' },
    { key: 'insUser', label: '등록자' },
    { key: 'insDate', label: '등록일시' },
    { key: 'updUser', label: '수정자' },
    { key: 'updDate', label: '수정일시' },
];

export const EMPTY_MESSAGE = '조회된 사용자가 없습니다.';

export const USE_YN_OPTIONS = [
    { value: '', label: '전체' },
    { value: USE_YN.YES, label: '사용' },
    { value: USE_YN.NO, label: '미사용' },
] as const;

export const USER_TYPE_OPTIONS = [
    { value: '', label: '전체' },
    { value: USER_TYPE.ADMIN, label: '관리자' },
    { value: USER_TYPE.HEAD, label: '본사' },
    { value: USER_TYPE.SUPPLIER, label: '협력사' },
    { value: USER_TYPE.RIDER, label: '라이더' },
] as const;

// ======== 초기값 ========

export const INITIAL_FILTERS: FilterState = {
    keyword: '',
    userType: '',
    useYn: '',
    suppName: '',
};

// ======== 헬퍼 함수 ========

/**
 * 필터에서 검색 API 파라미터 추출 (빈 값 제외)
 */
export const buildSearchParams = (filters: FilterState): Partial<Omit<FilterState, 'keyword'>> | undefined => {
    const params: Partial<Omit<FilterState, 'keyword'>> = {};
    if (filters.userType) params.userType = filters.userType;
    if (filters.useYn) params.useYn = filters.useYn;
    if (filters.suppName) params.suppName = filters.suppName;
    return Object.keys(params).length > 0 ? params : undefined;
};

/**
 * 권한 코드에 따른 배지 클래스 반환
 */
export const getPrmCodeBadgeClass = (prmCode: string): string => {
    switch (prmCode) {
        case PRM_CODE.HEAD:
        case PRM_CODE.ADMIN:
            return 'badge-info';
        case PRM_CODE.SUPPLIER:
            return 'badge-warning';
        default:
            return 'badge-success';
    }
};

/**
 * 사용 여부에 따른 배지 클래스 반환
 */
export const getUseYnBadgeClass = (useYn: string): string => {
    return useYn === USE_YN.YES ? 'badge-success' : 'badge-danger';
};

/**
 * 빈 값을 대시로 표시
 */
export const displayValue = (value: string | null | undefined): string => {
    return value || '-';
};

/**
 * 협력사 타입 표시 (협력사가 아닌 경우 '-')
 * userType이 협력사(S)인 경우 협력사 타입을 표시 (커스텀 권한 포함)
 */
export const displaySuppType = (userType: string | null, suppType: string | null | undefined): string => {
    // userType이 협력사(S)가 아니면 '-' 표시
    if (userType !== USER_TYPE.SUPPLIER) return '-';
    if (!suppType) return '-';
    return suppType === 'F' ? '가맹' : suppType === 'D' ? '직영' : suppType;
};

/**
 * 테이블 행 인덱스 계산
 */
export const calculateRowIndex = (
    currentPage: number,
    itemsPerPage: number,
    index: number
): number => {
    return (currentPage - 1) * itemsPerPage + index + 1;
};

/**
 * 권한 코드로 기반 사용자 타입(baseUserType) 반환
 * - 표준 권한: 코드 자체가 userType (A, C, S, R)
 * - SOD: 협력사 타입
 * - 커스텀 권한: val3(baseUserType) 반환
 */
export const getBaseUserTypeFromPrmCode = (
    prmCode: string,
    prmCodes: PrmCodeEntry[]
): string | null => {
    const code = prmCodes.find(c => c.comCode === prmCode);
    if (!code) return null;

    if (
        prmCode === PRM_CODE.ADMIN ||
        prmCode === PRM_CODE.HEAD ||
        prmCode === PRM_CODE.SUPPLIER ||
        prmCode === PRM_CODE.RIDER
    ) {
        return prmCode;
    }

    if (prmCode === PRM_CODE.SUPPLIER_ONLY_DASHBOARD) {
        return USER_TYPE.SUPPLIER;
    }

    return code.val3 || null;
};

/**
 * 대상 사용자 타입에 따라 권한 코드 표시 여부 반환
 * - 협력사 사용자: 협력사 관련 권한만 표시
 * - 라이더 사용자: 라이더 권한만 표시
 * - 본사/관리자: 본사·관리자 권한 표시
 */
export const filterPermissionCode = (
    code: PrmCodeEntry,
    isSupplierUserType: boolean,
    isRiderUserType: boolean,
): boolean => {
    const standardCodes: string[] = [
        PRM_CODE.ADMIN,
        PRM_CODE.HEAD,
        PRM_CODE.SUPPLIER,
        PRM_CODE.RIDER,
        PRM_CODE.SUPPLIER_ONLY_DASHBOARD,
    ];
    const isStandardCode = standardCodes.includes(code.comCode);

    if (isSupplierUserType) {
        if (isStandardCode) {
            return code.comCode === PRM_CODE.SUPPLIER || code.comCode === PRM_CODE.SUPPLIER_ONLY_DASHBOARD;
        }
        return code.val3 === USER_TYPE.SUPPLIER;
    }

    if (isRiderUserType) {
        if (isStandardCode) {
            return code.comCode === PRM_CODE.RIDER;
        }
        return code.val3 === USER_TYPE.RIDER;
    }

    // 본사(C) 또는 관리자(A)
    if (isStandardCode) {
        return (
            code.comCode === PRM_CODE.HEAD ||
            code.comCode === PRM_CODE.ADMIN ||
            code.comCode === PRM_CODE.SUPPLIER_ONLY_DASHBOARD
        );
    }
    return code.val3 === USER_TYPE.HEAD || code.val3 === USER_TYPE.ADMIN;
};
